import { WS_URL } from '../config/environment';

type EventHandler = (payload: unknown) => void;

interface PendingRequest {
  resolve: (value: unknown) => void;
  reject: (error: Error) => void;
}

interface QueuedRequest extends PendingRequest {
  requestId: string;
  action: string;
  data: Record<string, unknown>;
}

interface ServerMessage {
  requestId?: string;
  success?: boolean;
  data?: unknown;
  error?: { message: string };
  type?: string;
  payload?: unknown;
}

class BoardApiService {
  private ws: WebSocket | null = null;
  private boardId: string = '';
  private userId: string = '';
  private isConnecting: boolean = false;
  private connectionPromise: Promise<void> | null = null;
  private _reconnectAttempts: number = 0;
  private maxReconnectAttempts: number = 5;
  private reconnectDelay: number = 1000;
  private shouldReconnect: boolean = true;
  private sessionToken: string | null = null;
  private authSessionToken: string | null = null;
  private reconnectTimer: ReturnType<typeof setTimeout> | null = null;
  private pendingRequests: Map<string, PendingRequest> = new Map();
  private queuedRequests: QueuedRequest[] = [];
  private eventHandlers: Map<string, Set<EventHandler>> = new Map();
  private connectionStateHandlers: Set<(connected: boolean) => void> = new Set();

  get currentReconnectAttempts(): number {
    return this._reconnectAttempts;
  }

  setAuthSession(sessionToken: string | null): void {
    this.authSessionToken = sessionToken;
  }

  connect(boardId: string, userId: string): Promise<void> {
    if (this.boardId && this.boardId !== boardId) {
      this.sessionToken = null;
    }
    this.boardId = boardId;
    this.userId = userId;
    this.shouldReconnect = true;
    this._reconnectAttempts = 0;
    return this.createConnection();
  }

  private createConnection(): Promise<void> {
    if (this.connectionPromise) {
      return this.connectionPromise;
    }

    if (this.ws && this.ws.readyState === WebSocket.OPEN) {
      return Promise.resolve();
    }

    this.isConnecting = true;
    this.connectionPromise = new Promise((resolve, reject) => {
      let settled = false;
      const encodedUserId = encodeURIComponent(this.userId);
      const authQuery = this.authSessionToken
        ? `&authToken=${encodeURIComponent(this.authSessionToken)}`
        : '';
      const url = `${WS_URL}?userId=${encodedUserId}${authQuery}`;

      try {
        const socket = new WebSocket(url);
        this.ws = socket;

        socket.onopen = () => {
          if (this.ws !== socket) {
            if (!settled) {
              settled = true;
              reject(new Error('WebSocket connection closed'));
            }
            return;
          }

          void this.restoreSession()
            .catch((error) => {
              console.error('Failed to restore board session:', error);
              this.sessionToken = null;
            })
            .finally(() => {
              if (this.ws !== socket) {
                if (!settled) {
                  settled = true;
                  reject(new Error('WebSocket connection closed'));
                }
                return;
              }

              this.isConnecting = false;
              this.connectionPromise = null;
              this._reconnectAttempts = 0;
              this.notifyConnectionChange(true);
              this.flushQueuedRequests();
              settled = true;
              resolve();
            });
        };

        socket.onclose = () => {
          if (this.ws !== socket) {
            if (!settled) {
              settled = true;
              reject(new Error('WebSocket connection closed'));
            }
            return;
          }

          this.isConnecting = false;
          this.connectionPromise = null;
          this.ws = null;
          this.rejectPendingRequests(new Error('Connection closed'));
          this.notifyConnectionChange(false);

          if (this.shouldReconnect && this._reconnectAttempts < this.maxReconnectAttempts) {
            this.scheduleReconnect();
          } else {
            this.rejectQueuedRequests(new Error('WebSocket not connected'));
          }

          if (!settled) {
            settled = true;
            reject(new Error('WebSocket connection closed'));
          }
        };

        socket.onerror = () => {
          if (this.ws !== socket) {
            if (!settled) {
              settled = true;
              reject(new Error('WebSocket connection failed'));
            }
            return;
          }

          this.isConnecting = false;
          if (!settled && this._reconnectAttempts === 0) {
            settled = true;
            this.connectionPromise = null;
            reject(new Error('WebSocket connection failed'));
          }
        };

        socket.onmessage = (event) => {
          if (this.ws !== socket) return;

          try {
            const message = JSON.parse(event.data) as ServerMessage;
            this.handleMessage(message);
          } catch (err) {
            console.error('Failed to parse WebSocket message:', err);
          }
        };
      } catch (err) {
        this.isConnecting = false;
        this.connectionPromise = null;
        settled = true;
        reject(err instanceof Error ? err : new Error('WebSocket connection failed'));
      }
    });

    return this.connectionPromise;
  }

  private scheduleReconnect(): void {
    this._reconnectAttempts++;
    const delay = Math.min(this.reconnectDelay * Math.pow(2, this._reconnectAttempts - 1), 30000);
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
    }
    this.reconnectTimer = setTimeout(() => {
      this.reconnectTimer = null;
      if (this.shouldReconnect) {
        this.createConnection().catch(console.error);
      }
    }, delay);
  }

  private async restoreSession(): Promise<void> {
    if (!this.sessionToken || !this.boardId) return;
    const response = await this.send<{ sessionToken?: string }>('joinBoard', {
      boardId: this.boardId,
      sessionToken: this.sessionToken,
    });
    if (response.sessionToken) {
      this.sessionToken = response.sessionToken;
    }
  }

  private handleMessage(message: ServerMessage): void {
    if (message.requestId && this.pendingRequests.has(message.requestId)) {
      const pending = this.pendingRequests.get(message.requestId)!;
      this.pendingRequests.delete(message.requestId);
      if (message.success) {
        this.storeSessionToken(message.data);
        pending.resolve(message.data);
      } else {
        pending.reject(new Error(message.error?.message || 'Request failed'));
      }
      return;
    }
    if (message.type) {
      this.dispatchEvent(message.type, message.payload);
    }
  }

  private storeSessionToken(data: unknown): void {
    if (!data || typeof data !== 'object' || Array.isArray(data)) return;
    const token = (data as Record<string, unknown>).sessionToken;
    if (typeof token === 'string' && token.trim() !== '') {
      this.sessionToken = token;
    }
  }

  private dispatchEvent(type: string, payload: unknown): void {
    const handlers = this.eventHandlers.get(type);
    if (handlers) {
      handlers.forEach((handler) => handler(payload));
    }
  }

  private notifyConnectionChange(connected: boolean): void {
    this.connectionStateHandlers.forEach((handler) => handler(connected));
  }

  disconnect(): void {
    this.shouldReconnect = false;
    this.isConnecting = false;
    this.connectionPromise = null;
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }
    const socket = this.ws;
    this.ws = null;
    if (socket) {
      socket.close();
    }
    this.rejectPendingRequests(new Error('Connection closed'));
    this.rejectQueuedRequests(new Error('Connection closed'));
  }

  send<T = unknown>(action: string, data: Record<string, unknown> = {}): Promise<T> {
    return new Promise((resolve, reject) => {
      const requestId = crypto.randomUUID();
      const request: QueuedRequest = {
        requestId,
        action,
        data,
        resolve: (value: unknown) => resolve(value as T),
        reject,
      };

      if (!this.ws || this.ws.readyState !== WebSocket.OPEN) {
        if (this.shouldReconnect) {
          this.queuedRequests.push(request);
          if (!this.isConnecting) {
            void this.createConnection().catch(console.error);
          }
          return;
        }
        reject(new Error('WebSocket not connected'));
        return;
      }

      this.transmit(request);
    });
  }

  private transmit(request: QueuedRequest): void {
    if (!this.ws || this.ws.readyState !== WebSocket.OPEN) {
      this.queuedRequests.unshift(request);
      return;
    }

    this.pendingRequests.set(request.requestId, {
      resolve: request.resolve,
      reject: request.reject,
    });

    this.ws.send(JSON.stringify({
      action: request.action,
      requestId: request.requestId,
      data: this.withSessionToken(request.action, request.data),
    }));

    setTimeout(() => {
      if (this.pendingRequests.has(request.requestId)) {
        this.pendingRequests.delete(request.requestId);
        request.reject(new Error('Request timeout'));
      }
    }, 30000);
  }

  private withSessionToken(action: string, data: Record<string, unknown>): Record<string, unknown> {
    if (!this.sessionToken) {
      return data;
    }
    if (
      action === 'joinBoard'
      && (typeof data.sessionToken === 'string' || typeof data.password === 'string' || typeof data.adminPassword === 'string')
    ) {
      return data;
    }
    return {
      ...data,
      sessionToken: this.sessionToken,
    };
  }

  private flushQueuedRequests(): void {
    const queued = [...this.queuedRequests];
    this.queuedRequests = [];
    queued.forEach((request) => this.transmit(request));
  }

  private rejectPendingRequests(error: Error): void {
    this.pendingRequests.forEach((pending) => pending.reject(error));
    this.pendingRequests.clear();
  }

  private rejectQueuedRequests(error: Error): void {
    this.queuedRequests.forEach((queued) => queued.reject(error));
    this.queuedRequests = [];
  }

  onEvent(type: string, handler: EventHandler): () => void {
    if (!this.eventHandlers.has(type)) {
      this.eventHandlers.set(type, new Set());
    }
    this.eventHandlers.get(type)!.add(handler);
    return () => { this.eventHandlers.get(type)?.delete(handler); };
  }

  onConnectionChange(handler: (connected: boolean) => void): () => void {
    this.connectionStateHandlers.add(handler);
    return () => { this.connectionStateHandlers.delete(handler); };
  }

  get isConnected(): boolean {
    return this.ws !== null && this.ws.readyState === WebSocket.OPEN;
  }
}

export const boardApi = new BoardApiService();
